import { SensorLockerStatus } from '@frontend/device/types';
import { Functionality } from '@frontend/functionality/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';

import { useSlotListener } from '../../../hooks/useSlotListener/useSlotListener';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveSerialProcessContinueStep } from '../../utils/resolve-serial-process-continue';
import { useScreen } from '../useScreen';

interface ViewProps {
    functionality?: Functionality;
    continueEnabled?: boolean;
    onContinue?: () => void;
    showCenterContinue: boolean;
    onCenterContinue: () => void;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
    slot: Slot | null;
    slotStatus: SensorLockerStatus;
    waitingForSeconds: number | null;
    nextStep?: Step;
    screen_type?: string | undefined;
}

const useProcessOpenCloseSlot = (): ViewProps => {
    const { store, onBack, onSubmit, onContinue, continueEnabled, onHome, navigationState, screen_type, updateWizardData } = useScreen();
    const [slot, setSlot] = useState<Slot | null>(null);
    const expectedSlotIdRef = useRef<string | null>(null);
    const [slotStatus, setSlotStatus] = useState<SensorLockerStatus>(SensorLockerStatus.OPENING);
    const [waitingForSeconds, setWaitingForSeconds] = useState<number | null>(0);

    const { remoteTransaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoFetch: true
    });

    const { package: _package, localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoFetch: true,
        autoCreate: false
    });

    const applyStatusIfMatchingSlot = (pkgSlotId: string | null | undefined, status: SensorLockerStatus) => {
        const expectedSlotId = expectedSlotIdRef.current;
        if (expectedSlotId && pkgSlotId === expectedSlotId) {
            setSlotStatus(status);
        }
    };

    useSlotListener({
        id: 'PROCESS_OPEN_CLOSE_SLOT',
        onPackageSlotOpened(pkg) {
            applyStatusIfMatchingSlot(pkg.slot_id, SensorLockerStatus.OPENED);
        },
        onPackageSlotNotOpened(pkg) {
            applyStatusIfMatchingSlot(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
        },
        onPackageSlotClosed(pkg) {
            applyStatusIfMatchingSlot(pkg.slot_id, SensorLockerStatus.CLOSED);
        },
        onPackageSlotNotClosed(pkg) {
            applyStatusIfMatchingSlot(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
        }
    });

    useEffect(() => {
        if (!slot) {
            if (store && _package) {
                if (_package.spot_id && _package.slot_id && _package.account_id) {
                    SlotClient.fetchAccountSpotSlot(_package.account_id, _package.spot_id, _package.slot_id).then(setSlot);
                } else if (_package.slot_id) {
                    SlotClient.resolveSlots([_package.slot_id]).then((result) => {
                        setSlot(result.results[0] || null);
                    });
                }
            } else if (remoteTransaction) {
                if (remoteTransaction.spot_id && remoteTransaction.slot_id && remoteTransaction.account_id) {
                    SlotClient.fetchAccountSpotSlot(remoteTransaction.account_id, remoteTransaction.spot_id, remoteTransaction.slot_id).then(setSlot);
                } else if (remoteTransaction.slot_id) {
                    SlotClient.resolveSlots([remoteTransaction.slot_id]).then((result) => {
                        setSlot(result.results[0] || null);
                    });
                }
            }
        }
        const intervalId = setInterval(() => {
            setWaitingForSeconds((prev) => {
                if (prev === null) {
                    clearInterval(intervalId);
                    return null;
                }
                return prev + 1;
            });
        }, 1000);
        return () => {
            clearInterval(intervalId);
        };
    }, [_package, remoteTransaction]);

    useEffect(() => {
        expectedSlotIdRef.current = slot?.id ?? _package?.slot_id ?? remoteTransaction?.slot_id ?? null;
    }, [slot, _package?.slot_id, remoteTransaction?.slot_id]);

    // Reset slot UI when advancing to the next package in a serial loop.
    useEffect(() => {
        setSlot(null);
        setSlotStatus(SensorLockerStatus.OPENING);
        setWaitingForSeconds(0);
        expectedSlotIdRef.current = _package?.slot_id ?? null;
    }, [_package?.id]);

    const handleSubmit = async () => {
        if (!store || !screen_type) return;

        const nextStep = await resolveSerialProcessContinueStep({
            steps: store.steps,
            processScreenType: screen_type,
            wizardData: store.data as Record<string, unknown>,
            updatePackage: async (pkg) => {
                updateWizardData({ package: pkg });
                await localUpdatePackage(pkg);
            }
        });

        onSubmit(nextStep);
    };

    return {
        functionality: store?.functionality,
        continueEnabled,
        onContinue,
        showCenterContinue: continueEnabled || !!navigationState.user,
        onCenterContinue: continueEnabled ? onContinue : onHome,
        onSubmit: handleSubmit,
        onBack,
        slot,
        slotStatus,
        waitingForSeconds,
        nextStep:
            store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT) ??
            store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_TRUE) ??
            undefined,
        screen_type
    };
};

export default useProcessOpenCloseSlot;
